import { useState, type ReactNode } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { mutate } from '@/runtime'
import { KindError } from '@/kinds/kind'
import type { Hue, PaletteRecord } from '@/lib/colour'
import { paletteFor } from '@/lib/look'
import { useScope } from '@/state/scope'
import { allPacks, packsFor } from '@/packs'
import { LookPicker } from '@/components/LookPicker'
import { IconButtonField } from '@/components/EmojiPicker'
import { Button, Field, Sheet } from '@/components/ui'
import { workspaceSpec, type WorkspaceSpec } from './schema'
import { CORE_NAV, navAfterTick, navTicks } from '@/lib/nav'
import type { WorkspaceResource } from './apply'
import { slugify } from '@/suites'

type FormInput = z.input<typeof workspaceSpec>

/**
 * A suite's settings (a workspace in the data model). `extra` sits above the
 * buttons: Settings → Suites puts the Home reset there.
 */
export const WorkspaceForm = ({
  resource,
  onDone,
  extra,
}: {
  resource?: WorkspaceResource
  onDone: () => void
  extra?: ReactNode
}) => {
  const [error, setError] = useState<string | null>(null)
  const setWs = useScope((s) => s.setWs)
  const { register, handleSubmit, control, watch, setValue, formState } = useForm<
    FormInput,
    unknown,
    WorkspaceSpec
  >({
    resolver: zodResolver(workspaceSpec),
    defaultValues: resource
      ? { ...resource.spec }
      : { name: '', slug: '', colour: 'violet', packs: [], order: 0 },
  })
  const name = watch('name')
  const packs = watch('packs')
  const submit = async (v: WorkspaceSpec) => {
    setError(null)
    try {
      if (resource) {
        const patch: Record<string, unknown | null> = {}
        for (const key of [
          'name',
          'icon',
          'colour',
          'packs',
          'today_cap',
          'order',
          'nav',
          'theme',
          'palette',
        ] as const) {
          if (JSON.stringify(resource.spec[key]) !== JSON.stringify(v[key]))
            patch[key] = v[key] === undefined ? null : v[key]
        }
        if (Object.keys(patch).length) await mutate.patch(resource.id, { spec: patch })
      } else {
        const r = await mutate.create('Workspace', { ...v, slug: v.slug || slugify(v.name) })
        setWs((r.spec as WorkspaceSpec).slug)
      }
      onDone()
    } catch (e) {
      setError(e instanceof KindError ? e.message : (e as Error).message)
    }
  }
  return (
    <Sheet open onClose={onDone} title={resource ? 'Edit suite' : 'New suite'}>
      <form onSubmit={handleSubmit(submit)} noValidate>
        <div className="flex gap-3 items-start">
          <Controller
            control={control}
            name="icon"
            render={({ field }) => (
              <IconButtonField value={field.value ?? undefined} onChange={field.onChange} />
            )}
          />
          <div className="flex-1">
            <Field label="Name" error={formState.errors.name?.message}>
              <input
                {...register('name', {
                  onChange: (e) => {
                    if (!resource) setValue('slug', slugify(e.target.value))
                  },
                })}
                className="w-full"
                placeholder="DevOS"
                autoFocus={!resource}
                data-testid="workspace-name"
              />
            </Field>
          </div>
        </div>
        <Field
          label="Slug"
          hint={
            resource
              ? 'Fixed after creation'
              : `Used in URLs: /?ws=${watch('slug') || slugify(name ?? '') || '…'}`
          }
          error={formState.errors.slug?.message}
        >
          <input
            {...register('slug')}
            className="w-full font-mono"
            disabled={!!resource}
            data-testid="workspace-slug"
          />
        </Field>
        <LookPicker
          value={{
            theme: watch('theme'),
            palette: paletteFor(watch('theme'), watch('palette') as PaletteRecord | undefined),
            colour: watch('colour') as Hue,
          }}
          fallbackHue="violet"
          onChange={(l) => {
            setValue('theme', l.theme as WorkspaceSpec['theme'])
            setValue('palette', l.palette)
            setValue('colour', l.colour ?? 'violet')
          }}
        />
        {allPacks().length > 0 && (
          <Field label="Packs" hint="Extra kinds and views for this suite">
            <Controller
              control={control}
              name="packs"
              render={({ field }) => (
                <div className="flex flex-col gap-1">
                  {allPacks().map((p) => (
                    <label key={p.name} className="flex items-center gap-2 text-sm min-h-9">
                      <input
                        type="checkbox"
                        checked={(field.value ?? []).includes(p.name)}
                        onChange={(e) =>
                          field.onChange(
                            e.target.checked
                              ? [...(field.value ?? []), p.name]
                              : (field.value ?? []).filter((x) => x !== p.name),
                          )
                        }
                      />
                      <span className="font-medium">{p.title}</span>
                      <span className="text-faint">{p.description}</span>
                    </label>
                  ))}
                </div>
              )}
            />
          </Field>
        )}
        <Field label="Pages" hint="Untick what this suite doesn't need; Settings is always there">
          <Controller
            control={control}
            name="nav"
            render={({ field }) => {
              // the apps as they are now (merged ones live inside their app)
              const shown = CORE_NAV.map((n) => n.key)
              // what the packs hide with no hand-set list shows unticked, as the sidebar has it
              const hidden = packsFor(packs ?? []).flatMap((p) => p.layout?.hides ?? [])
              const ticked = navTicks(shown, field.value, hidden)
              return (
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                  {CORE_NAV.map(({ key: k, label }) => (
                    <label key={k} className="flex items-center gap-2 text-sm min-h-8">
                      <input
                        type="checkbox"
                        checked={ticked.has(k)}
                        onChange={(e) =>
                          field.onChange(navAfterTick(shown, field.value, hidden, k, e.target.checked))
                        }
                        data-testid={`nav-${k}`}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              )
            }}
          />
        </Field>
        <Field label="Today shows at most" hint="Leave empty to use the global setting">
          <input
            type="number"
            min={1}
            max={20}
            {...register('today_cap', { setValueAs: (v: string) => (v === '' ? undefined : Number(v)) })}
            className="w-24"
          />
        </Field>
        {extra}
        {error && <p className="text-sm text-rose-300 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button onClick={onDone}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            disabled={formState.isSubmitting}
            data-testid="workspace-save"
          >
            Save
          </Button>
        </div>
      </form>
    </Sheet>
  )
}
